import { PageIcon } from "@/components/PageIcon";
import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Truck, RefreshCw, Loader2, Users, Clock, CheckCircle2, ExternalLink, UserPlus, AlertTriangle, ClipboardList, History } from "lucide-react";
import { AssignStaffDialog } from "@/components/AssignStaffDialog";
import { useAuth } from "@/context/AuthContext";

const today = () => new Date().toLocaleDateString("en-CA", { timeZone: "Europe/London" });
const n = (v) => Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 2 });
const STATUS = {
  loading: { label: "Loading", cls: "bg-amber-50 text-amber-700 ring-amber-200" },
  confirmed: { label: "Loaded", cls: "bg-sky-50 text-sky-700 ring-sky-200" },
  departed: { label: "Departed", cls: "bg-emerald-50 text-emerald-700 ring-emerald-200" },
};

function Tile({ label, value, testId }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid={testId}>
      <div className="text-xs font-medium uppercase tracking-wide text-slate-500">{label}</div>
      <div className="mt-1 text-2xl font-semibold tabular-nums text-slate-900">{value}</div>
    </div>
  );
}

function LoadersLine({ shiftId, vehicleId, loaders, onAssign, testid }) {
  const { canAny } = useAuth();
  return (
    <div className="mt-2 flex flex-wrap items-center gap-2 text-sm text-slate-600" data-testid={testid}>
      <UserPlus className="h-4 w-4 text-slate-400" />
      <span>{loaders?.length ? `Loaders: ${loaders.map((x) => x.name).join(", ")}` : "No loader assigned"}</span>
      {shiftId && canAny(["deliveries.manage", "operations.manage"]) && (
        <button onClick={() => onAssign({ shiftId, vehicleId, current: (loaders || []).map((x) => x.id) })}
          className="text-xs font-medium text-indigo-600 hover:underline" data-testid={`${testid}-assign`}>{loaders?.length ? "Change" : "Assign loaders"}</button>
      )}
    </div>
  );
}

function LoadCard({ l, onAssign }) {
  const pct = l.required_qty ? Math.min(100, Math.round((l.loaded_qty / l.required_qty) * 100)) : 0;
  const st = STATUS[l.status] || { label: l.status, cls: "bg-slate-50 text-slate-600 ring-slate-200" };
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 transition-shadow hover:shadow-md" data-testid={`load-card-${l.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-lg font-semibold text-slate-900">
            <Truck className="h-5 w-5 text-indigo-500" />
            <span data-testid={`load-reg-${l.id}`}>{l.vehicle_registration || l.vehicle_id}</span>
          </div>
          <div className="mt-0.5 truncate text-xs text-slate-500">{l.vehicle_id}{l.shift_id ? ` · shift ${l.shift_id}` : ""}</div>
        </div>
        <span className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-medium ring-1 ${st.cls}`} data-testid={`load-status-${l.id}`}>{st.label}</span>
      </div>
      <div className="mt-4">
        <div className="flex items-baseline justify-between text-sm">
          <span className="text-slate-600">Loaded</span>
          <span className="font-semibold tabular-nums text-slate-900" data-testid={`load-progress-${l.id}`}>{n(l.loaded_qty)} / {n(l.required_qty)}</span>
        </div>
        <div className="mt-1.5 h-2.5 overflow-hidden rounded-full bg-slate-100">
          <div className={`h-full rounded-full transition-[width] duration-500 ${pct >= 100 ? "bg-emerald-500" : "bg-indigo-500"}`} style={{ width: `${pct}%` }} />
        </div>
        <div className="mt-1 text-xs text-slate-500">{l.lines_done} of {l.line_count} lines complete</div>
      </div>
      {l.engineers.length > 0 && (
        <div className="mt-3 flex items-center gap-1.5 text-sm text-slate-600"><Users className="h-4 w-4 text-slate-400" />{l.engineers.join(", ")}</div>
      )}
      <LoadersLine shiftId={l.shift_id} vehicleId={l.vehicle_id} loaders={l.loaders} onAssign={onAssign} testid={`load-loaders-${l.id}`} />
      {l.jobs.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {l.jobs.map((j) => (
            <span key={j.legacy_id} className="rounded-md bg-slate-50 px-2 py-0.5 text-xs text-slate-600 ring-1 ring-slate-200">{j.legacy_id}{j.customer ? ` · ${j.customer}` : ""}</span>
          ))}
        </div>
      )}
      <Link to={`/loading/${l.id}`} className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-indigo-600 hover:text-indigo-700" data-testid={`load-open-${l.id}`}>
        Open load sheet <ExternalLink className="h-3.5 w-3.5" />
      </Link>
    </div>
  );
}

const ACTION_TILES = [
  ["ready_to_load", "Ready to load"], ["loading", "Loading now"], ["fully_loaded", "Fully loaded"],
  ["outstanding_picking", "Outstanding picking"], ["missing_products", "Missing products"],
  ["discrepancies", "Discrepancies"], ["intervention", "Needs intervention"],
];

function ActionSummary({ s, missing, disc }) {
  if (!s) return null;
  const why = [s.no_loader && `${s.no_loader} awaiting load with no loader`, s.no_picker && `${s.no_picker} pick list(s) with no picker`].filter(Boolean);
  return (
    <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-4" data-testid="load-board-action-summary">
      <h2 className="flex items-center gap-2 text-sm font-semibold text-slate-900"><AlertTriangle className="h-4 w-4 text-amber-500" />Action required</h2>
      <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-4 xl:grid-cols-7">
        {ACTION_TILES.map(([k, label]) => (
          <div key={k} className={`rounded-xl px-3 py-2 ${["missing_products", "discrepancies", "intervention"].includes(k) && s[k] ? "bg-rose-50 text-rose-800" : "bg-slate-50 text-slate-800"}`} data-testid={`action-${k}`}>
            <div className="text-[11px] font-medium uppercase tracking-wide opacity-70">{label}</div>
            <div className="text-xl font-bold">{s[k] || 0}</div>
          </div>
        ))}
      </div>
      {why.length > 0 && <div className="mt-3 text-xs text-amber-700" data-testid="action-why">{why.join(" · ")}</div>}
      {missing.length > 0 && (
        <div className="mt-3" data-testid="action-missing-list">
          <div className="text-xs font-semibold text-rose-800">Missing products</div>
          {missing.map((m) => <div key={m.id} className="text-sm text-slate-700">{m.product || m.sku} · {m.qty_short} short{m.vehicle_id ? ` · van ${m.vehicle_id}` : ""}{m.job_legacy_id ? ` · job ${m.job_legacy_id}` : ""}{m.reason ? ` · ${m.reason}` : ""}</div>)}
        </div>
      )}
      {disc.length > 0 && (
        <div className="mt-3" data-testid="action-discrepancy-list">
          <div className="text-xs font-semibold text-rose-800">Picking and loading discrepancies</div>
          {disc.map((d) => <div key={d.key} className="text-sm text-slate-700">{d.kind === "picking" ? "Picking" : "Loading"}{d.vehicle_id ? ` · van ${d.vehicle_id}` : ""}{d.title ? ` · ${d.title}` : ""} · {d.detail}{d.count > 1 ? ` (×${d.count})` : ""}</div>)}
        </div>
      )}
    </div>
  );
}

function AssignmentHistory({ date, rev }) {
  const [items, setItems] = useState(null);
  useEffect(() => {
    api.get("/assignment-history", { params: { date } }).then((r) => setItems(r.data.items)).catch(() => setItems([]));
  }, [date, rev]);
  const list = (xs) => (xs?.length ? xs.join(", ") : "Nobody");
  return (
    <div className="mt-8" data-testid="assignment-history">
      <h2 className="flex items-center gap-2 text-base font-semibold text-slate-900"><History className="h-4 w-4 text-indigo-500" />Assignment history</h2>
      <div className="mt-3 overflow-x-auto rounded-2xl border border-slate-200 bg-white">
        {!items ? <div className="p-4 text-sm text-slate-400">Loading…</div> : items.length === 0 ? (
          <div className="p-4 text-sm text-slate-500" data-testid="assignment-history-empty">No picker or loader changes for this date.</div>
        ) : (
          <table className="w-full min-w-[560px] text-sm">
            <thead><tr className="text-left text-xs uppercase tracking-wide text-slate-500"><th className="px-4 py-2">When</th><th className="px-4 py-2">What</th><th className="px-4 py-2">Before</th><th className="px-4 py-2">After</th><th className="px-4 py-2">Changed by</th></tr></thead>
            <tbody className="divide-y divide-slate-100">
              {items.map((h, i) => (
                <tr key={i} data-testid={`assignment-history-row-${i}`}>
                  <td className="px-4 py-2 whitespace-nowrap text-slate-600">{new Date(h.at).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}</td>
                  <td className="px-4 py-2 text-slate-800">{h.kind === "picker" ? `Picker · list ${(h.pick_list_id || "").slice(0, 8)}` : `Loaders · van ${h.vehicle_id} · shift ${h.shift_id}`}</td>
                  <td className="px-4 py-2 text-slate-500">{list(h.before)}</td>
                  <td className="px-4 py-2 font-medium text-slate-800">{list(h.after)}</td>
                  <td className="px-4 py-2 text-slate-600">{h.by}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export default function LoadBoard() {
  const [date, setDate] = useState(today());
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [loaderDlg, setLoaderDlg] = useState(null);
  const [pickerDlg, setPickerDlg] = useState(null);
  const [rev, setRev] = useState(0);
  const { canAny } = useAuth();
  const canAssign = canAny(["deliveries.manage", "operations.manage"]);
  const load = useCallback(() => {
    setBusy(true);
    api.get("/loading/board", { params: { date } }).then((r) => setData(r.data)).catch(() => setData({ loads: [], awaiting: [] })).finally(() => setBusy(false));
    setRev((v) => v + 1);
  }, [date]);
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, [load]);
  const loads = data?.loads || [];
  const req = loads.reduce((s, l) => s + l.required_qty, 0);
  const got = loads.reduce((s, l) => s + l.loaded_qty, 0);
  return (
    <div className="px-6 lg:px-8 pt-3 lg:pt-4 pb-28 lg:pb-8" data-testid="load-board-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-3 text-2xl font-bold tracking-tight text-slate-900"><PageIcon />Load Board</h1>
          <p className="mt-1 text-sm text-slate-500">Loading progress for every van run. Refreshes every 30 seconds.</p>
        </div>
        <div className="flex items-center gap-2">
          <Input type="date" value={date} onChange={(e) => setDate(e.target.value || today())} className="w-44" data-testid="load-board-date" />
          <button onClick={load} className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-slate-200 px-3 text-sm text-slate-700 hover:bg-slate-50" data-testid="load-board-refresh">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Refresh
          </button>
        </div>
      </div>
      <div className="mt-6 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Tile label="Van runs" value={loads.length} testId="load-board-runs" />
        <Tile label="Units loaded" value={`${n(got)} / ${n(req)}`} testId="load-board-units" />
        <Tile label="Departed" value={loads.filter((l) => l.status === "departed").length} testId="load-board-departed" />
        <Tile label="Awaiting load" value={(data?.awaiting || []).length} testId="load-board-awaiting-count" />
      </div>
      {data && <ActionSummary s={data.summary} missing={data.missing || []} disc={data.discrepancies || []} />}
      {!data ? (
        <div className="mt-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
      ) : (
        <>
          <div className="mt-6 grid gap-4 md:grid-cols-2 xl:grid-cols-3" data-testid="load-board-loads">
            {loads.map((l) => <LoadCard key={l.id} l={l} onAssign={setLoaderDlg} />)}
            {loads.length === 0 && (
              <div className="col-span-full rounded-2xl border border-dashed border-slate-300 p-10 text-center text-sm text-slate-500" data-testid="load-board-empty">No loads started for this date.</div>
            )}
          </div>
          {data.awaiting.length > 0 && (
            <div className="mt-8" data-testid="load-board-awaiting">
              <h2 className="flex items-center gap-2 text-base font-semibold text-slate-900"><Clock className="h-4 w-4 text-amber-500" />Picked and waiting to load</h2>
              <div className="mt-3 divide-y divide-slate-100 rounded-2xl border border-slate-200 bg-white">
                {data.awaiting.map((w) => (
                  <div key={w.shift_id || w.vehicle_id} className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 text-sm" data-testid={`awaiting-${w.shift_id || w.vehicle_id}`}>
                    <span className="font-medium text-slate-800">{w.vehicle_id || "No van"}{w.shift_id ? <span className="ml-2 font-normal text-slate-500">shift {w.shift_id}</span> : null}</span>
                    <span className="flex items-center gap-1.5 text-slate-600"><CheckCircle2 className="h-4 w-4 text-emerald-500" />{w.pick_lists} pick list{w.pick_lists === 1 ? "" : "s"} · {n(w.picked_qty)} units</span>
                    <div className="w-full -mt-1"><LoadersLine shiftId={w.shift_id} vehicleId={w.vehicle_id} loaders={w.loaders} onAssign={setLoaderDlg} testid={`awaiting-loaders-${w.shift_id || w.vehicle_id}`} /></div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {(data.data_issues || []).length > 0 && (
            <div className="mt-8 rounded-2xl border border-rose-200 bg-rose-50 p-4" data-testid="load-board-data-issues">
              <h2 className="flex items-center gap-2 text-sm font-semibold text-rose-800"><AlertTriangle className="h-4 w-4" />Lines that cannot be loaded</h2>
              {data.data_issues.map((d) => <div key={d.key} className="mt-1 text-sm text-rose-700">{d.title || d.sku || "Unnamed line"}: {d.detail} (pick list {d.pick_list_id.slice(0, 8)})</div>)}
            </div>
          )}
          {(data.pick_lists || []).length > 0 && (
            <div className="mt-8" data-testid="load-board-pick-lists">
              <h2 className="flex items-center gap-2 text-base font-semibold text-slate-900"><ClipboardList className="h-4 w-4 text-indigo-500" />Pick lists and pickers</h2>
              <div className="mt-3 divide-y divide-slate-100 rounded-2xl border border-slate-200 bg-white">
                {data.pick_lists.map((p) => (
                  <div key={p.id} className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 text-sm" data-testid={`pick-row-${p.id}`}>
                    <span className="font-medium text-slate-800">{p.id.slice(0, 8)} <span className="font-normal text-slate-500">{p.vehicle_id ? `van ${p.vehicle_id}` : "no van"}{p.shift_id ? ` · shift ${p.shift_id}` : ""} · {p.status}</span></span>
                    <span className="flex items-center gap-2 text-slate-600" data-testid={`pick-picker-${p.id}`}>{p.assigned_picker_name ? `Picker: ${p.assigned_picker_name}` : "No picker"}
                      {canAssign && <button onClick={() => setPickerDlg(p)} className="text-xs font-medium text-indigo-600 hover:underline" data-testid={`pick-assign-${p.id}`}>{p.assigned_picker_name ? "Change" : "Assign picker"}</button>}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {canAssign && <AssignmentHistory date={date} rev={rev} />}
        </>
      )}
      <AssignStaffDialog open={!!loaderDlg} onClose={() => setLoaderDlg(null)} multi perm="deliveries.load" testid="assign-loaders-dialog"
        title={loaderDlg ? `Loaders for van ${loaderDlg.vehicleId} · shift ${loaderDlg.shiftId}` : ""} current={loaderDlg?.current || []}
        endpoint={loaderDlg ? `/shift-loading/${loaderDlg.shiftId}/vehicles/${loaderDlg.vehicleId}/loaders` : ""} bodyFor={(ids) => ({ user_ids: ids })} onSaved={load} />
      <AssignStaffDialog open={!!pickerDlg} onClose={() => setPickerDlg(null)} perm="mobile.picking" testid="assign-picker-dialog"
        title={pickerDlg ? `Picker for pick list ${pickerDlg.id.slice(0, 8)}` : ""} current={pickerDlg?.assigned_picker_id ? [pickerDlg.assigned_picker_id] : []}
        endpoint={pickerDlg ? `/pick-lists/${pickerDlg.id}/picker` : ""} bodyFor={(ids) => ({ user_id: ids[0] || null })} onSaved={load} />
    </div>
  );
}
